import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminBilingualInput from '@/components/admin/AdminBilingualInput';
import AdminImageUpload from '@/components/admin/AdminImageUpload';
import AdminMultiImageUpload from '@/components/admin/AdminMultiImageUpload';
import toast from 'react-hot-toast';
import type { BilingualText, Artwork } from '@/types';

const INPUT_CLS = "w-full px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20";
const LABEL_CLS = "block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2";

const AdminDropshopEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const collections = useAdminStore((s) => s.dropshopCollections);
  const artists = useAdminStore((s) => s.artists);
  const allArtworks = useAdminStore((s) => s.artworks);
  const addCollection = useAdminStore((s) => s.addDropshopCollection);
  const updateCollection = useAdminStore((s) => s.updateDropshopCollection);
  const fieldLimits = useAdminStore((s) => s.settings?.fieldLimits);

  const isEditing = id !== undefined && id !== 'new';
  const existing = isEditing ? collections.find((c) => c._id === id) : undefined;

  const [title, setTitle] = useState<BilingualText>({ en: '', ka: '' });
  const [description, setDescription] = useState<BilingualText>({ en: '', ka: '' });
  const [artistId, setArtistId] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [selectedArtworkIds, setSelectedArtworkIds] = useState<string[]>([]);

  useEffect(() => {
    if (existing) {
      setTitle(existing.title);
      setDescription(existing.description);
      const aId = typeof existing.artist === 'string' ? existing.artist : existing.artist?._id || '';
      setArtistId(aId);
      setCoverImage(existing.coverImage);
      setImages(existing.images);
      setVideoUrl(existing.videoUrl || '');
      setReleaseDate(existing.releaseDate);
      setIsActive(existing.isActive);
      setSelectedArtworkIds(existing.artworkIds || []);
    }
  }, [existing]);

  const toggleArtwork = (artworkId: string): void => {
    setSelectedArtworkIds((prev) =>
      prev.includes(artworkId) ? prev.filter((aid) => aid !== artworkId) : [...prev, artworkId]
    );
  };

  // Filter artworks by selected artist
  const artistArtworks: Artwork[] = artistId
    ? allArtworks.filter((a) => {
        const aid = typeof a.artist === 'string' ? a.artist : a.artist?._id;
        return aid === artistId;
      })
    : allArtworks;

  const handleSave = (): void => {
    if (!title.en.trim()) {
      toast.error('Title (EN) is required');
      return;
    }

    const data = {
      title,
      description,
      artist: artistId,
      coverImage,
      images,
      videoUrl,
      releaseDate,
      isActive,
      artworkIds: selectedArtworkIds,
    };

    if (isEditing && existing) {
      updateCollection(existing._id, data);
      toast.success('Collection updated!');
    } else {
      addCollection(data);
      toast.success('Collection created!');
    }
    navigate('/admin/dropshop');
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-4">
        <Link to="/admin/dropshop" className="w-9 h-9 rounded-xl border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white hover:border-black/15 dark:hover:border-white/15 transition-all">
          <ArrowLeft size={16} />
        </Link>
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">
            {isEditing ? 'Edit Collection' : 'New Collection'}
          </h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">
            {isEditing ? existing?.title.en : 'Create a dropshop collection'}
          </p>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-6 space-y-5">
        <AdminBilingualInput label="Title" value={title} onChange={setTitle} placeholder="Collection title" maxLength={fieldLimits?.title} />
        <AdminBilingualInput label="Description" value={description} onChange={setDescription} textarea placeholder="Collection description / story" maxLength={fieldLimits?.description} />

        {/* Artist select */}
        <div>
          <label className={LABEL_CLS}>Artist</label>
          <select value={artistId} onChange={(e) => setArtistId(e.target.value)} className={INPUT_CLS}>
            <option value="">— Select artist —</option>
            {artists.map((a) => (
              <option key={a._id} value={a._id}>{a.name.en || a.name.ka}</option>
            ))}
          </select>
        </div>

        <AdminImageUpload label="Cover Image" value={coverImage} onChange={setCoverImage} shape="rect" />
        <AdminMultiImageUpload label="Gallery Images" value={images} onChange={setImages} />

        {/* Video URL */}
        <div>
          <label className={LABEL_CLS}>Video URL (optional)</label>
          <input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://..." className={INPUT_CLS} />
        </div>

        {/* Release Date */}
        <div>
          <label className={LABEL_CLS}>Release Date</label>
          <input type="date" value={releaseDate} onChange={(e) => setReleaseDate(e.target.value)} className={INPUT_CLS} />
        </div>

        {/* Active toggle */}
        <div className="flex items-center justify-between">
          <div>
            <label className={LABEL_CLS}>Active</label>
            <p className="text-[10px] text-black/30 dark:text-white/30">Show this collection on the public Dropshop page</p>
          </div>
          <button onClick={() => setIsActive(!isActive)} className={`w-10 h-5 rounded-full transition-all ${isActive ? 'bg-black dark:bg-white' : 'bg-black/10 dark:bg-white/10'}`}>
            <span className={`block w-4 h-4 rounded-full bg-white dark:bg-black transition-transform ${isActive ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>

        {/* Artwork picker */}
        <div>
          <label className={LABEL_CLS}>Linked Artworks ({selectedArtworkIds.length} selected)</label>
          <p className="text-[9px] text-black/25 dark:text-white/25 mb-2">
            {artistId ? 'Showing artworks by selected artist' : 'Select an artist above to filter, or pick from all artworks'}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
            {artistArtworks.map((aw) => {
              const selected = selectedArtworkIds.includes(aw._id);
              return (
                <label
                  key={aw._id}
                  className={`flex items-center gap-2 p-2 rounded-xl border cursor-pointer transition-all ${
                    selected
                      ? 'border-black/20 dark:border-white/20 bg-black/[0.03] dark:bg-white/[0.05]'
                      : 'border-black/[0.06] dark:border-white/[0.06] hover:border-black/10 dark:hover:border-white/10'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleArtwork(aw._id)}
                    className="w-3.5 h-3.5 rounded accent-black dark:accent-white shrink-0"
                  />
                  <div className="flex items-center gap-2 min-w-0">
                    {aw.images?.[0] && (
                      <img src={aw.images?.[0] || ''} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0" />
                    )}
                    <span className="text-[10px] font-medium truncate">{aw.title.en || aw.title.ka || 'Untitled'}</span>
                  </div>
                </label>
              );
            })}
          </div>
          {artistArtworks.length === 0 && (
            <p className="text-[10px] text-black/30 dark:text-white/30 mt-2">No artworks available{artistId ? ' for this artist' : ''}.</p>
          )}
        </div>
      </motion.div>

      <div className="flex items-center gap-3">
        <button onClick={handleSave} className="flex items-center gap-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity">
          <Save size={14} /> {isEditing ? 'Save Changes' : 'Create Collection'}
        </button>
        <Link to="/admin/dropshop" className="px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors">Cancel</Link>
      </div>
    </div>
  );
};

export default AdminDropshopEditorPage;
